import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isValid,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";

/** Defaults when query params are absent (clean URLs). */
export const DEFAULT_TIME_OVERVIEW_PERIOD = "month";

export const TIME_OVERVIEW_PERIODS = [
  "day",
  "week",
  "month",
  "quarter",
  "year",
  "custom",
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_OVERVIEW_PARAM_KEYS = ["period", "date", "to", "all"];

/**
 * Union React Router params with the live window query.
 * Prefer the window value for `tab` (deep links / last written tab) and fill
 * in any other keys the router copy is missing. Do not let a stale window
 * overwrite newer router keys such as `period`.
 *
 * @param {URLSearchParams} routerParams
 * @param {string} [windowSearch]
 */
export function mergeRouterAndWindowSearchParams(
  routerParams,
  windowSearch = typeof window !== "undefined" ? window.location.search : ""
) {
  const combined = new URLSearchParams(routerParams);
  const fromWindow = new URLSearchParams(windowSearch);
  fromWindow.forEach((value, key) => {
    if (key === "tab" || !combined.has(key)) {
      combined.set(key, value);
    }
  });
  return combined;
}

export function parseLocalIsoDate(raw) {
  if (!raw || !ISO_DATE.test(String(raw))) return null;
  const d = new Date(`${raw}T12:00:00`);
  return isValid(d) ? d : null;
}

export function toIsoDate(d) {
  return format(d, "yyyy-MM-dd");
}

export function defaultTimeOverviewDate(now = new Date()) {
  return toIsoDate(startOfMonth(now));
}

export function parseTimeOverviewPeriod(raw) {
  const v = String(raw || "").toLowerCase();
  return TIME_OVERVIEW_PERIODS.includes(v) ? v : DEFAULT_TIME_OVERVIEW_PERIOD;
}

export function rangeForPeriod(period, dateIso, toIso = null) {
  const kind = parseTimeOverviewPeriod(period);
  const anchor = parseLocalIsoDate(dateIso) || new Date();

  switch (kind) {
    case "day":
      return { dateFrom: toIsoDate(anchor), dateTo: toIsoDate(anchor) };
    case "week": {
      const from = startOfWeek(anchor, { weekStartsOn: 1 });
      const to = endOfWeek(anchor, { weekStartsOn: 1 });
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "quarter": {
      const from = startOfQuarter(anchor);
      const to = endOfQuarter(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "year": {
      const from = startOfYear(anchor);
      const to = endOfYear(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
    case "custom": {
      const start = parseLocalIsoDate(dateIso) || anchor;
      const end = parseLocalIsoDate(toIso) || start;
      const [a, b] = start <= end ? [start, end] : [end, start];
      return { dateFrom: toIsoDate(a), dateTo: toIsoDate(b) };
    }
    default: {
      const from = startOfMonth(anchor);
      const to = endOfMonth(anchor);
      return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
    }
  }
}

/**
 * @param {URLSearchParams} sp
 * @param {Date} [now]
 */
export function parseProjectTimeOverviewSearchParams(sp, now = new Date()) {
  const all = sp.get("all") === "1" || sp.get("all") === "true";
  const period = parseTimeOverviewPeriod(sp.get("period"));
  const date = parseLocalIsoDate(sp.get("date"))
    ? String(sp.get("date"))
    : defaultTimeOverviewDate(now);
  const toRaw = parseLocalIsoDate(sp.get("to")) ? String(sp.get("to")) : null;
  const { dateFrom, dateTo } = rangeForPeriod(period, date, toRaw);

  return {
    all,
    period,
    date,
    dateFrom: all ? null : dateFrom,
    dateTo: all ? null : dateTo,
  };
}

/**
 * Merge time-overview keys without dropping unrelated query params (e.g. `tab`).
 *
 * @param {URLSearchParams} prev
 * @param {{ all?: boolean; period?: string | null; date?: string | null; to?: string | null }} patch
 * @param {Date} [now]
 */
export function mergeProjectTimeOverviewSearchParams(prev, patch, now = new Date()) {
  const current = parseProjectTimeOverviewSearchParams(prev, now);

  const all = patch.all !== undefined ? Boolean(patch.all) : current.all;
  const period =
    patch.period !== undefined
      ? parseTimeOverviewPeriod(patch.period)
      : current.period;
  const date =
    patch.date !== undefined
      ? parseLocalIsoDate(patch.date)
        ? String(patch.date)
        : defaultTimeOverviewDate(now)
      : current.date;
  const to =
    patch.to !== undefined
      ? parseLocalIsoDate(patch.to)
        ? String(patch.to)
        : null
      : period === "custom"
        ? current.dateTo
        : null;

  const next = new URLSearchParams(prev);
  TIME_OVERVIEW_PARAM_KEYS.forEach((key) => next.delete(key));

  if (all) {
    next.set("all", "1");
    return next;
  }

  if (period !== DEFAULT_TIME_OVERVIEW_PERIOD) next.set("period", period);

  const isDefaultMonth =
    period === DEFAULT_TIME_OVERVIEW_PERIOD && date === defaultTimeOverviewDate(now);
  if (!isDefaultMonth) next.set("date", date);

  if (period === "custom") {
    const { dateTo } = rangeForPeriod("custom", date, to);
    next.set("date", date);
    next.set("period", "custom");
    next.set("to", dateTo);
  }

  return next;
}
